// Complete Authentication Helper Functions for Admin Product Creation
import { getApiBaseUrlWithoutPath } from '../../config/environment';

/**
 * Admin Login Function
 * Logs in as admin and stores token in localStorage
 */
export const getAdminToken = async () => {
  try {
    console.log('🔐 Attempting admin login...');
    
    const apiUrl = getApiBaseUrlWithoutPath();
    
    const response = await fetch(`${apiUrl}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: 'admin@superwear.com', // Replace with your admin email
        password: 'admin123' // Replace with your admin password
      })
    });
    
    const result = await response.json();
    
    console.log('📊 Login response:', result);
    
    if (result.success) {
      console.log('✅ Admin login successful');
      localStorage.setItem('adminToken', result.token);
      localStorage.setItem('adminUser', JSON.stringify(result.user));
      return result.token;
    } else {
      console.error('❌ Admin login failed:', result.error);
      throw new Error(result.error || 'Login failed');
    }
  } catch (error) {
    console.error('❌ Login error:', error);
    throw error;
  }
};

/**
 * Token Validation Function
 * Validates if the current token is valid and user has admin panel access (admin or team)
 */
export const validateToken = async (token) => {
  try {
    const apiUrl = getApiBaseUrlWithoutPath();
    
    const response = await fetch(`${apiUrl}/api/auth/me`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    const result = await response.json();
    
    // Handle non-OK responses (401, 403, etc.)
    if (!response.ok) {
      console.log('❌ Token validation failed:', response.status, result);
      return false;
    }
    
    // Backend returns user in result.user (admin-login/team) or result.data (some endpoints)
    const userData = result.user || result.data;
    const role = userData?.role;
    
    // Accept both admin and team - both can access admin panel
    if (result.success && (role === 'admin' || role === 'team')) {
      console.log('✅ Token is valid, user:', role);
      return true;
    } else {
      console.log('❌ Token invalid or user has no admin panel access, role:', role);
      return false;
    }
  } catch (error) {
    console.error('❌ Token validation error:', error);
    return false;
  }
};

/**
 * Get Valid Admin Token
 * Returns existing valid token. Does NOT auto-login as admin - user must login via form.
 * Team members and admins both use their own tokens.
 */
export const getValidAdminToken = async () => {
  try {
    const token = localStorage.getItem('adminToken');
    
    if (!token) {
      throw new Error('Please login to continue.');
    }
    
    // Validate token (accepts both admin and team)
    const isValid = await validateToken(token);
    if (!isValid) {
      throw new Error('Session expired. Please login again.');
    }
    
    return token;
  } catch (error) {
    console.error('❌ Authentication error:', error);
    throw new Error(error.message || 'Authentication failed. Please login.');
  }
};

/**
 * Check Authentication Status
 * Returns true if user is authenticated as admin
 */
export const checkAuthStatus = async () => {
  try {
    const token = localStorage.getItem('adminToken');
    if (!token) return false;
    
    return await validateToken(token);
  } catch (error) {
    console.error('❌ Auth check error:', error);
    return false;
  }
};

/**
 * Logout Function
 * Clears stored authentication data
 */
export const logoutAdmin = () => {
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminUser');
  console.log('🚪 Admin logged out');
};

/**
 * Test Authentication
 * Tests the complete authentication flow
 */
export const testAuthentication = async () => {
  try {
    console.log('🧪 Testing authentication...');
    
    const token = await getAdminToken();
    console.log('✅ Authentication successful');
    console.log('🔑 Token:', token.substring(0, 20) + '...');
    
    const isValid = await validateToken(token);
    console.log('✅ Token validation:', isValid ? 'Valid' : 'Invalid');
    
    return token;
  } catch (error) {
    console.error('❌ Authentication failed:', error);
    throw error;
  }
};
